import { inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { concat, defer, EMPTY, exhaustMap, filter, ignoreElements, map, pipe, switchMap, takeUntil, tap } from 'rxjs'

import { isDefined } from '@app/core/is-defined'
import type { S3BatchFileUpload } from '@app/modules/s3-upload/core/s3-batch-file-upload'
import type { BatchFileUploadErrorSummary } from '@app/modules/upload/core'
import type { AudioRenderInfo } from '@app/modules/upload/models'
import { ToastService } from '@app/overlays/toast/toast.service'

import { voiceUploadEvents } from './voice-upload.events'
import { UrlBlobCacheStore } from '../../../../upload/services/url-blob-cache.store'
import { conversationEvents } from '../../../components/store/conversation.events'
import { injectDefaultErrorHandler } from '../../../components/store/default-error-handler'
import type { Conversation } from '../../../models/conversation.model'
import type { UnsentVoiceMessage } from '../models/voice-message.model'
import { VoiceMessageService } from '../services/voice-message.service'

interface State {
  voiceUploadInProgress: boolean
  voiceUploadError: BatchFileUploadErrorSummary | undefined
}

const initialState: State = {
  voiceUploadInProgress: false,
  voiceUploadError: undefined,
}

export const voiceUploadFeature = () => {
  return signalStoreFeature(
    withState(initialState),
    withMethods(
      (
        store,
        voiceMessageService = inject(VoiceMessageService),
        dispatch = injectDispatch(voiceUploadEvents),
        events = inject(Events),
        defaultErrorHandler = injectDefaultErrorHandler(),
        urlBlobCacheStore = inject(UrlBlobCacheStore),
      ) => ({
        handleVoiceUpload: rxMethod<{
          batchUpload: S3BatchFileUpload
          conversation: Conversation
          flow: string
        }>(
          pipe(
            exhaustMap(({ batchUpload, conversation, flow }) => {
              dispatch.uploadStart()
              let voice: UnsentVoiceMessage | undefined = undefined

              return concat(
                batchUpload.uploadChange$.pipe(ignoreElements()),
                defer(() => {
                  const successful = batchUpload.successful
                  if (successful.length === 0) {
                    return EMPTY
                  }

                  const { mediaRenderInfo$ } = batchUpload.successful[0]
                  urlBlobCacheStore.add(successful)

                  return mediaRenderInfo$.pipe(
                    filter((payload): payload is AudioRenderInfo => 'duration' in payload),
                    switchMap(({ duration }) =>
                      voiceMessageService
                        .uploadVoice({
                          transloadit: batchUpload.assembly,
                          successful: successful.map(u => u.media),
                          profile_browse_id: conversation.profile.browse_id,
                        })
                        .pipe(
                          tapResponse({
                            next: response => {
                              voice = {
                                ...response.media[0],
                                duration,
                                flow,
                              }
                            },
                            error: defaultErrorHandler,
                          }),
                        ),
                    ),
                  )
                }),
              ).pipe(
                tap({
                  complete: () =>
                    dispatch.uploadComplete({
                      batchUpload,
                      voice,
                      profile: conversation.profile,
                      flow,
                    }),
                  finalize: () => dispatch.uploadEnd(),
                }),
                takeUntil(events.on(conversationEvents.select)),
              )
            }),
          ),
        ),
        clearVoiceUploadError: () => patchState(store, { voiceUploadError: undefined }),
      }),
    ),
    withReducer(
      on(voiceUploadEvents.uploadStart, () => ({ voiceUploadInProgress: true })),
      on(voiceUploadEvents.uploadEnd, () => ({ voiceUploadInProgress: false })),
    ),
    withEventHandlers((_store, events = inject(Events), toastService = inject(ToastService)) => ({
      errorOnUploadComplete$: events.on(voiceUploadEvents.uploadComplete).pipe(
        map(({ payload }) => payload.batchUpload.getErrorSummary()),
        filter(isDefined),
        tap(error => toastService.show(`Voice failed to upload. ${error.messages.join(' ')}`)),
      ),
    })),
  )
}
